import { useState } from 'react';
import {
  ATTENDANCE_PERIODS,
  type AttendanceDayView,
  type AttendancePeriod,
  type AttendanceSchedule,
  dayLabel,
  duration,
  shortTime,
  useEmployeeAttendance,
} from '../../api/attendance';
import { AttendanceDayDetail } from '../../components/AttendanceDayDetail';
import { Icon } from '../../components/Icon';
import { KpiCard } from '../../components/Stats';
import { EmptyState, ErrorState, LoadingState } from '../../components/states';

export function AttendancePeriodPills({
  value,
  onChange,
}: {
  value: AttendancePeriod;
  onChange: (days: AttendancePeriod) => void;
}) {
  return (
    <fieldset className="pill-row period-pills">
      <legend className="visually-hidden">Период</legend>
      {ATTENDANCE_PERIODS.map((period) => (
        <button
          key={period.days}
          type="button"
          className="pill-toggle"
          aria-pressed={period.days === value}
          onClick={() => onChange(period.days)}
        >
          {period.label}
        </button>
      ))}
    </fieldset>
  );
}

/** Сколько дней показывать до нажатия «Показать все дни». */
const VISIBLE_DAYS = 7;

const minutesOfDay = (time: string) => {
  const [hours = 0, minutes = 0] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * График приходов: точка на день. По вертикали — время прихода, красным — опоздания,
 * пунктир — контрольное время из графика сотрудника.
 */
function ArrivalsChart({
  items,
  schedule,
}: {
  items: AttendanceDayView[];
  schedule: AttendanceSchedule | null;
}) {
  const days = items
    .filter((day) => !day.weekend && day.firstAt)
    .slice(0, 30)
    .reverse();
  if (days.length < 2) return null;
  const arrivals = days.map((day) => minutesOfDay(day.firstAt ?? '00:00'));
  const lateAfter = schedule?.lateAfter ? minutesOfDay(schedule.lateAfter) : null;
  const top = Math.min(...arrivals, lateAfter ?? Number.POSITIVE_INFINITY) - 30;
  const bottom = Math.max(...arrivals, lateAfter ?? 0) + 30;
  const span = Math.max(bottom - top, 60);
  const y = (minutes: number) => ((minutes - top) / span) * 100;
  const clock = (minutes: number) =>
    `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(Math.round(minutes % 60)).padStart(2, '0')}`;

  return (
    <div className="arrivals">
      <div className="arrivals-head">
        <h3>Приходы по дням</h3>
        <span className="muted">{days.length} дн.</span>
      </div>
      <div className="arrivals-chart">
        <span className="arrivals-scale arrivals-top">{clock(top)}</span>
        <span className="arrivals-scale arrivals-bottom">{clock(bottom)}</span>
        {lateAfter !== null ? (
          <span className="arrivals-limit" style={{ top: `${y(lateAfter)}%` }} title="Опоздание после" />
        ) : null}
        {days.map((day, index) => (
          <span
            key={day.date}
            className={`arrivals-dot${day.lateMinutes != null ? ' late' : ''}`}
            style={{
              left: `${(index / (days.length - 1)) * 100}%`,
              top: `${y(minutesOfDay(day.firstAt ?? '00:00'))}%`,
            }}
            title={`${dayLabel(day.date)}: приход ${shortTime(day.firstAt)}`}
          />
        ))}
      </div>
      <div className="arrivals-days">
        <span>{dayLabel(days[0]?.date ?? '')}</span>
        <span>{dayLabel(days.at(-1)?.date ?? '')}</span>
      </div>
    </div>
  );
}

/** Сегодняшние приход и последняя отметка — для шапки кабинета. */
export function AttendanceToday({ employeeId }: { employeeId: string }) {
  const attendance = useEmployeeAttendance(employeeId, 30);
  const data = attendance.data;
  if (!data?.linked) return null;
  const today = data.today;
  return (
    <div>
      <dt>Сегодня</dt>
      <dd className="today-line">
        {today ? (
          <>
            приход {shortTime(today.firstAt)}
            {today.passes.length > 1
              ? ` · последняя отметка ${shortTime(today.latestAt)} — ${today.passes.at(-1)?.kind === 'in' ? 'вход' : 'выход'}`
              : ''}
            {today.lateMinutes != null ? (
              <span className="status status-bad">опоздание {duration(today.lateMinutes)}</span>
            ) : null}
          </>
        ) : (
          <span className="muted">отметок пока нет</span>
        )}
      </dd>
    </div>
  );
}

/** Посещения по данным системы проходов: как считываются проходы и что из этого следует. */
export function AttendanceSection({ employeeId }: { employeeId: string }) {
  const [days, setDays] = useState<AttendancePeriod>(30);
  const [opened, setOpened] = useState<string | null>(null);
  // Дни свёрнуты: сразу видна последняя неделя, остальное — по кнопке.
  const [allDays, setAllDays] = useState(false);
  const attendance = useEmployeeAttendance(employeeId, days);
  const data = attendance.data;
  const summary = data?.summary;

  return (
    <section id="attendance" className="panel">
      <div className="panel-header">
        <h2>Посещения</h2>
        {data?.linked ? <AttendancePeriodPills value={days} onChange={setDays} /> : null}
      </div>
      {attendance.isPending ? <LoadingState label="Загружаю посещения" /> : null}
      {attendance.isError ? (
        <ErrorState error={attendance.error} onRetry={() => void attendance.refetch()} />
      ) : null}
      {data && !data.linked ? (
        <EmptyState title="Нет данных системы проходов">
          Сотрудник ещё не связан с системой учёта проходов.
        </EmptyState>
      ) : null}
      {data?.linked && summary ? (
        <>
          <div className="kpi-grid five task-kpis">
            <KpiCard
              label="Рабочих дней"
              icon="check"
              value={summary.workdays}
              extra={summary.expectedWorkdays ? `из ${summary.expectedWorkdays}` : undefined}
            />
            {summary.onTimeShare != null ? (
              <KpiCard
                label="Вовремя"
                icon="clock"
                value={`${summary.onTimeShare} %`}
                extra={summary.lateDays ? `опозданий ${summary.lateDays}` : 'без опозданий'}
                tone={summary.lateDays ? 'bad' : 'good'}
              />
            ) : null}
            <KpiCard label="Средний приход" icon="clock" value={summary.avgFirst ?? '—'} />
            <KpiCard label="Средний уход" icon="clock" value={summary.avgLast ?? '—'} />
            <KpiCard label="В офисе в среднем" icon="activity" value={duration(summary.avgMinutes)} />
          </div>

          <div className="attendance-facts">
            {data.schedule?.lateAfter ? (
              <span>
                График: начало {shortTime(data.schedule.workStart)}, опоздание после{' '}
                {shortTime(data.schedule.lateAfter)}
              </span>
            ) : null}
            {summary.missedWorkdays.length ? (
              <span className="text-bad">
                Рабочие дни без отметок: {summary.missedWorkdays.map(dayLabel).join('; ')}
              </span>
            ) : summary.expectedWorkdays ? (
              <span>Пропущенных рабочих дней нет</span>
            ) : null}
            {summary.avgExits != null ? (
              <span>
                Выходов в среднем за день: {String(summary.avgExits).replace('.', ',')}, вне офиса{' '}
                {duration(summary.avgOutsideMinutes)}
              </span>
            ) : null}
            {summary.unpairedDays ? <span>С пропущенной отметкой: {summary.unpairedDays} дн.</span> : null}
            {summary.weekendDays ? <span>В выходные: {summary.weekendDays} дн.</span> : null}
            {data.coverage ? (
              <span className="muted">
                Данные есть с {dayLabel(data.coverage.from)} по {dayLabel(data.coverage.to)}
              </span>
            ) : null}
          </div>

          {summary.workdays ? (
            <div className="weekday-grid">
              {summary.byWeekday.map((day) => (
                <div key={day.weekday} className="weekday-cell">
                  <span className="weekday-name">{day.weekday}</span>
                  <span title="Средний приход">{day.avgFirst ?? '—'}</span>
                  <span title="Средний уход">{day.avgLast ?? '—'}</span>
                  <span className="weekday-days">{day.days} дн.</span>
                </div>
              ))}
            </div>
          ) : null}

          {data.items.length ? <ArrivalsChart items={data.items} schedule={data.schedule} /> : null}

          {data.items.length ? (
            <>
              <div className="table-scroll">
                <table className="attendance-table">
                  <thead>
                    <tr>
                      <th>День</th>
                      <th className="number">Вход</th>
                      <th className="number">Уход</th>
                      <th className="number">В офисе</th>
                      <th className="number">Выходы</th>
                      <th>
                        <span className="visually-hidden">Подробнее</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(allDays ? data.items : data.items.slice(0, VISIBLE_DAYS)).map((day) => (
                      <tr
                        key={day.date}
                        className={`clickable${day.weekend ? ' weekend-row' : ''}`}
                        onClick={() => setOpened(day.date)}
                      >
                        <td>
                          <button
                            type="button"
                            className="row-link"
                            onClick={(event) => {
                              event.stopPropagation();
                              setOpened(day.date);
                            }}
                          >
                            <strong>{dayLabel(day.date)}</strong>
                          </button>
                          {day.weekend ? <small>выходной</small> : null}
                          {day.open ? <small>день идёт</small> : null}
                        </td>
                        <td className="number">
                          {shortTime(day.firstAt)}
                          {day.assumedArrival ? <small className="muted">не отмечен</small> : null}
                          {day.lateMinutes != null ? (
                            <small className="late-note">опоздание {duration(day.lateMinutes)}</small>
                          ) : null}
                        </td>
                        <td className="number">
                          {day.open ? (
                            <span className="muted">пока {shortTime(day.latestAt)}</span>
                          ) : day.lastAt ? (
                            shortTime(day.lastAt)
                          ) : (
                            <span className="status status-bad">нет отметки</span>
                          )}
                        </td>
                        <td className="number">{duration(day.minutes)}</td>
                        <td className="number">
                          {day.breaks.length ? (
                            <>
                              {day.breaks.length}
                              <small>{duration(day.outsideMinutes)}</small>
                            </>
                          ) : (
                            <span className="muted">—</span>
                          )}
                        </td>
                        <td className="link-cell">
                          <Icon name="chevronRight" size={14} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {data.items.length > VISIBLE_DAYS ? (
                <button type="button" className="btn-secondary more-row" onClick={() => setAllDays(!allDays)}>
                  <Icon name={allDays ? 'minus' : 'plus'} size={14} />
                  {allDays ? 'Свернуть дни' : `Показать все дни — ещё ${data.items.length - VISIBLE_DAYS}`}
                </button>
              ) : null}
            </>
          ) : (
            <EmptyState title="За период отметок нет" />
          )}
        </>
      ) : null}
      {opened && data
        ? (() => {
            const day = data.items.find((item) => item.date === opened);
            return day ? (
              <AttendanceDayDetail day={day} schedule={data.schedule} onClose={() => setOpened(null)} />
            ) : null;
          })()
        : null}
    </section>
  );
}
